import { OnboardingScreen } from './screens';

/**
 * Plain shape consumed by `deriveOnboardingScreen`. Decoupled from generated
 * Apollo types so the function is trivially unit-testable and survives query
 * shape changes — the hook adapts its `useQuery` result into this shape.
 *
 * `dependents` are derived from the current user's `CaregiverRelationship`
 * rows in the hook (not fetched from `family.dependants` directly). The
 * `createDependantAndRelationship` mutation creates the dependent + caregiver
 * link atomically, so every dependent the user knows about is by definition
 * already linked — querying both lists would be redundant and creates a
 * "dependent missing user link" check that's impossible to fail in practice.
 */
export interface IOnboardingProxyData {
  user: {
    id: string;
    phoneNumber: string | null;
  };
  /**
   * `true` when the user has at least one residence row flagged
   * `isPrimary`. The wizard requires a primary address before advancing —
   * see the gating rule in {@link deriveOnboardingScreen}.
   */
  hasPrimaryResidence: boolean;
  dependents: ReadonlyArray<{ id: string }>;
  dependantPartnerships: ReadonlyArray<{
    dependents: ReadonlyArray<{ id: string }>;
  }>;
  /**
   * `true` when the loved-ones residence step has been answered — any
   * `livesWithCaregiver` or partnership `liveTogether` flag is non-null.
   * Relies on `DependentResidenceStep`'s `isFormReady` invariant: Continue is
   * blocked until the "live with you?" answer is non-null, so a completed
   * (not skipped) step always writes one of these. If that form rule is ever
   * relaxed this signal must be hardened, or users get stuck on residence.
   */
  hasDependentResidenceInfo: boolean;
  vehicleCount: number;
  financialAccountCount: number;
  /**
   * `true` when the household holds a **presentable** subscription — the signal
   * that checkout has been reached, not that it entitles. A lapsed row counts,
   * so a lapsed household has passed `PAYMENT` and is asked to restart its
   * membership in settings rather than sent back through the wizard. An
   * in-flight or failed attempt does not: `getLatestForFamily` excludes
   * `CREATING` and `FAILED`, so an abandoned checkout still owes `PAYMENT`.
   */
  hasSubscription: boolean;
  /**
   * `true` when this household must pay and has not: the wall applies to it,
   * `feat-billing-checkout` is open so a subscribe path exists, and the
   * subscription read succeeded.
   *
   * `PAYMENT` is dropped from the tail entirely when it is false, which covers
   * three cases at once. Checkout closed: every subscribe path sits behind that
   * gate, so the screen's own button could not work, and a pricing page that
   * refuses is worse than no pricing page — `hasSubscription` is unreadable
   * then too, since the query serving it is skipped. The wall not applying: a
   * household nobody is asking to pay is not resumed onto pricing, which is
   * what keeps every local dev out of the wizard, the dev map having checkout
   * open and the wall shut. And a failed subscription read, which cannot answer
   * the question at all.
   */
  paymentRequired: boolean;
}

/**
 * Derive the next onboarding screen for a partially-onboarded user, or
 * `null` if the user is fully onboarded.
 *
 * Gating rules (checked in order; the first unmet rule wins):
 *   1. Phone number entered (`phoneNumber` non-null) → otherwise
 *      `PHONE_NUMBER`. Fresh users land here naturally. We deliberately
 *      do NOT gate on `phoneNumberVerifiedAt`: `PHONE_NUMBER_VERIFY` is a
 *      forward-only screen in the wizard sequence (like `GREAT_START` and
 *      `ALMOST_THERE`) and never a derive target — verification is optional,
 *      and a user who entered their phone but didn't verify resumes at the
 *      next data gate.
 *   2. Primary residence saved (`hasPrimaryResidence`) → otherwise
 *      `USER_RESIDENCE`. The proxy query reads `user.primaryResidence` —
 *      its presence is the only signal the resume derivation has that this
 *      step has been completed.
 *   3. At least one dependent linked to the current user → otherwise
 *      `DEPENDENT_COUNT`. The count-picker is the entry point to the
 *      loved-ones phase; once a dependent exists, the count step is
 *      implicitly satisfied. The wizard's linear order takes the user from
 *      `DEPENDENT_COUNT` → `DEPENDENT_ENTRY` → `DEPENDENT_REVIEW`.
 *   4. When the user has exactly two dependents, they're linked to each
 *      other via a `DependantPartnership` → otherwise `DEPENDENT_COUNT`.
 *      The partnership link is fired on the review step; users land back
 *      at the count picker (and forward through entry → review) until both
 *      dependents are connected. Users with 3+ dependents don't currently
 *      have a pair-link requirement (the product rule is "if there are
 *      two, link them"); revisit if the wizard adds multi-partner support.
 *   5. The optional loved-ones tail — `DEPENDENT_RESIDENCE`, `DEPENDENT_VEHICLE`
 *      — followed by the `FINANCIAL_ACCT_CONNECT` and `PAYMENT` gates, is resolved by
 *      **frontier resume** rather than first-unmet. The loved-ones screens can
 *      be skipped (the "Skip for now" button writes nothing — `PAYMENT` offers
 *      no such action), so for those, data presence can't distinguish
 *      "skipped" from "never reached." We find
 *      the furthest screen in the tail that has data and resume at the next one:
 *      a screen counts as satisfied if it has its own data OR any later screen
 *      does. This never fast-forwards past an unfilled screen, and once the user
 *      has progressed past a skipped screen they aren't yanked back to it.
 *   6. Tail fully satisfied (its last entry has data) → `null` (onboarded).
 *
 * `DEPENDENT_REVIEW` and `DEPENDENT_ENTRY` carry no completion signal of their
 * own — the dependent-count gate covers both — so they are never derive
 * targets. The frontier collapse below assumes the tail is contiguous, so its
 * entries are appended in the order {@link ONBOARDING_SCREEN_ORDER} walks them
 * and `PAYMENT` is last.
 *
 * `PAYMENT` sits past the financial gate in both orders, and its data signal is
 * the household holding a subscription. It is in the tail only while the household
 * must actually pay — see {@link IOnboardingProxyData.paymentRequired}. Without
 * that, the financial gate is the last entry again and a connected account
 * derives `null`. So a household nobody is asking to pay is never shown a
 * pricing screen, and one the wall applies to that bailed before subscribing
 * resumes on it rather than reading as onboarded.
 */
export const deriveOnboardingScreen = (data: IOnboardingProxyData): OnboardingScreen | null => {
  const {
    user,
    hasPrimaryResidence,
    dependents,
    dependantPartnerships,
    hasDependentResidenceInfo,
    vehicleCount,
    financialAccountCount,
    hasSubscription,
    paymentRequired,
  } = data;

  if (user.phoneNumber === null) {
    return OnboardingScreen.PHONE_NUMBER;
  }
  if (!hasPrimaryResidence) {
    return OnboardingScreen.USER_RESIDENCE;
  }
  if (dependents.length === 0) {
    return OnboardingScreen.DEPENDENT_COUNT;
  }

  // Two-dependent pair-link requirement. `DependantPartnership.dependants` is
  // a variable-length array on the wire; for the two-dependent rule, we just
  // need a partnership whose dependents set contains both of the user's
  // dependents.
  if (dependents.length === 2) {
    const [a, b] = dependents;
    const linked = dependantPartnerships.some((p) => {
      const ids = new Set(p.dependents.map((d) => d.id));
      return ids.has(a.id) && ids.has(b.id);
    });
    if (!linked) {
      return OnboardingScreen.DEPENDENT_COUNT;
    }
  }

  // Frontier resume over the optional tail. Resume at the screen just past the
  // furthest one that has data, which is the financial slot for a household
  // with no connected account and nothing after it — a subscription is later in
  // the tail, so holding one derives `null` with the account still unconnected.
  // What counts as onboarded is gate-dependent: with
  // `PAYMENT` in the tail, a connected account alone leaves pricing unmet and an
  // unsubscribed viewer resumes there — only data on the last entry derives
  // `null`, and which screen that is depends on `paymentRequired`.
  const tail: ReadonlyArray<{ screen: OnboardingScreen; hasData: boolean }> = [
    { screen: OnboardingScreen.DEPENDENT_RESIDENCE, hasData: hasDependentResidenceInfo },
    { screen: OnboardingScreen.DEPENDENT_VEHICLE, hasData: vehicleCount > 0 },
    { screen: OnboardingScreen.FINANCIAL_ACCT_CONNECT, hasData: financialAccountCount > 0 },
    ...(paymentRequired ? [{ screen: OnboardingScreen.PAYMENT, hasData: hasSubscription }] : []),
  ];
  const lastDataIdx = tail.reduce((acc, entry, i) => (entry.hasData ? i : acc), -1);

  return lastDataIdx === tail.length - 1 ? null : tail[lastDataIdx + 1].screen;
};
